/**
 * Benevity sends cause names HTML-encoded ("LA JOLLA ART &amp; WINE…") in every query that returns
 * them (SPO-1301, mobile SPO-1293). Vue escapes text, so the entity would show as typed.
 */

/** Decodes the entities Benevity uses. `&amp;` goes last so `&amp;lt;` stays `&lt;`. */
export function decodeHtmlEntities(text: string): string {
  if (!text.includes('&')) return text;
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, '\'')
    .replace(/&apos;/g, '\'')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

/** Fields that hold a Benevity cause name on any type. */
const CAUSE_NAME_FIELDS = new Set(['causeName', 'benevityCausesName']);

/** Types whose `name` is a Benevity cause name (other types' `name` is user text). */
const CAUSE_TYPES = new Set([
  'BenevityCause',
  'BenevityCauseAttributes',
  'StickerCampaignCause',
  'FollowingGroupNominationCauseResultType',
]);

function isCauseNameField(key: string, typename: unknown): boolean {
  return CAUSE_NAME_FIELDS.has(key)
    || (key === 'name' && typeof typename === 'string' && CAUSE_TYPES.has(typename));
}

/**
 * Returns a copy of a GraphQL `data` tree with cause names decoded. Other values are kept as they
 * are, and unchanged branches are returned as the same object.
 */
export function decodeCauseNames<T>(value: T): T {
  if (Array.isArray(value)) {
    let changed = false;
    const items = value.map((item) => {
      const decoded = decodeCauseNames(item);
      if (decoded !== item) changed = true;
      return decoded;
    });
    return (changed ? items : value) as T;
  }
  if (value === null || typeof value !== 'object') return value;

  const source = value as Record<string, unknown>;
  let result: Record<string, unknown> | null = null;
  Object.keys(source).forEach((key) => {
    const field = source[key];
    const decoded = typeof field === 'string'
      ? (isCauseNameField(key, source.__typename) ? decodeHtmlEntities(field) : field)
      : decodeCauseNames(field);
    if (decoded !== field) {
      result = result ?? { ...source };
      result[key] = decoded;
    }
  });
  return (result ?? value) as T;
}
